import React, { useEffect } from 'react';
import { Tea, useTeaEditor, TeaListener } from '@bynder/tea-pot-lib';
import { useToken } from '../../queries/identity';
import themesetQueries, { useGetThemesetColors } from '../../queries/themesets';
import { TextStyle } from '@bynder/tea-pot-lib/src/data/configs/textStyles';
import { Typography, TypographyHeading } from '../../types/typography';
import { getTypographyWeight } from '../shared/styled-components/constants';
import { useTheme } from 'styled-components';
import type { ColorOption } from '@bynder/tea-pot-lib/src/data/configs/colors';
import { notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { LinkCopiedToClipboardNotification } from '../../types/notifications.tsx';
import { EXTERNAL_ROUTES } from '../../constants/app/index.ts';
import { useActiveChapter, useActiveGuide, useActivePage, useActiveWidget } from '../../queries/guides.ts';
import { optimisticallyUpdateWidget } from '../../mutations/helpers.ts';
import { WIDGET_TYPE } from '../../types/widgets.ts';
import { Page } from '../../types/pages.ts';
import { Guide } from '../../types/guides.ts';


interface Props {
  widgetId: string;
  sectionId: string;
  columnIndex: number;
  readOnly: boolean;
  document?: string;
  html?: string;
}

const TeaWidget: React.FC<Props> = ({
  widgetId,
  sectionId,
  columnIndex,
  readOnly,
  document,
  html,
}) => {
  const { data: guide } = useActiveGuide();
  const chapter = useActiveChapter();
  const { data: page } = useActivePage()

  const meta: { [key: string]: unknown } = {
    module: 'cxuc',
    guide_id: guide?.id,
    guide_title: guide?.title,
    chapter_id: chapter?.id,
    chapter_title: chapter?.title,
    page_id: page?.id,
    page_title: page?.title,
    section_id: sectionId,
    widget_id: widgetId,
  };

  const { translate } = useTranslate();
  const theme = useTheme();
  const themesetTypographies = themesetQueries.getThemesetTypographies();

  const themesetColors = useGetThemesetColors();
  const colours = themesetColors.map<ColorOption>(color => {
    return { label: color.label, value: color.hexCode };
  });

  const textStyles = Object.keys(themesetTypographies).map(
    (key: string): TextStyle => {
      const typography: Typography = themesetTypographies[key];
      let meta = typography.fontFamily + ' • ' + typography.fontSize + 'px';
      if (typography.heading === TypographyHeading.H2)
        meta = translate('THEMESET.ANCHOR_LINK_TEXT') + ' • ' + meta;

      return {
        fontWeight: getTypographyWeight(
          theme,
          typography.fontFamily,
          typography.fontStyle,
        ),
        label: typography.label,
        level:
          typography.heading === TypographyHeading.NORMAL
            ? 0
            : parseInt(typography.heading.substring(1)),
        meta
      };
    },
  );
  // Swap the order of Paragraph and H6 text styles so the array
  // is in the following order: H1, H2, H3, H4, H5, Paragraph, H6
  [textStyles[5], textStyles[6]] = [textStyles[6], textStyles[5]];

  const { data: accessToken } = useToken();
  const editorFormats = {
    heading: {
      allowAnchoredHeadings: true,
      allowedAnchoredHeadingLevels: [2]
    },
    list: {
      autoColourMarkers: true
    },
    textStyles: textStyles,
    colors: colours,
  }

  const { editor } = useTeaEditor(widgetId);
  const activeWidget = useActiveWidget();

  useAnchoredHeadingClickHandler(guide, page, widgetId, readOnly);
  useWatchAndUpdateDocumentsFromEditor(guide, page, sectionId, columnIndex, widgetId);

  useEffect(() => {
    if (!activeWidget || !editor) {
      return;
    }

    if (activeWidget.id === widgetId) {
      editor.commands.focus();
    }
  }, [activeWidget]);

  return (
    <div className="editor-wrapper">
      <Tea
        documentUuid={widgetId}
        documentGroupId={page?.id}
        meta={meta}
        accessToken={accessToken}
        readOnly={readOnly}
        document={document ? JSON.parse(document) : null}
        html={html}
        removeDefaultFocusStyle
        editorFormats={editorFormats}
      >
        <Tea.Toolbar editorFormats={editorFormats} />
      </Tea>
    </div>
  );
};

export default TeaWidget;

const findAnchoredHeading = (element: HTMLElement) => {
  let currentElement = element;

  // Traverse up the DOM tree
  while (currentElement) {
    if (currentElement.dataset.anchored) {
      return currentElement;
    }

    // If we reach a button, we stop checking further up
    // as button is the highest element that can be clicked
    if (currentElement.tagName === 'button') {
      return null;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

const handleAnchorHeaderClick = (
  guideId: string,
  pageId: string,
  anchorElement: HTMLElement,
) => {
  window.navigator.clipboard
    .writeText(`${EXTERNAL_ROUTES.PAGE(guideId, pageId)}#${anchorElement.id}`)
    .then(() => notify(LinkCopiedToClipboardNotification))
    .catch(e => console.error('Error copying link to clipboard', e));
};

function useWatchAndUpdateDocumentsFromEditor(guide: Guide, page: Page, sectionId: string, columnIndex: number, widgetId: string) {
  useEffect(() => {
    const teaListener = new TeaListener(widgetId);
    if (!teaListener) return;

    const { unsubscribe } = teaListener.on('saving', (event) => {
      const { html, document } = event;
      optimisticallyUpdateWidget(
        guide.id,
        page.id,
        sectionId,
        columnIndex,
        widgetId,
        WIDGET_TYPE.TEA,
        {
          document: document,
          html: html,
        }
      )
    });

    return () => {
      unsubscribe();
    }
  }, [widgetId, guide, page, sectionId, columnIndex]);
}

function useAnchoredHeadingClickHandler(guide: Guide, page: Page, widgetId: string, readOnly: boolean) {
  useEffect(() => {
    const teaListener = new TeaListener(widgetId);
    if (!teaListener) return;

    const { unsubscribe } = teaListener.on('element-clicked', event => {
      if (!readOnly) return;

      const anchoredElement = findAnchoredHeading(event.element)

      if (!anchoredElement) return;

      const element = anchoredElement.parentElement;

      handleAnchorHeaderClick(guide?.id, page?.id, element);
    });

    return () => {
      unsubscribe();
    }
  }, [widgetId, guide, page, readOnly]);
}
